/**
 * User Model
 *
 * Represents a user in the system with their profile information
 * and role-based access.
 */

export interface User {
  id: number;
  username: string;  // Email-based username
  firstName: string;
  lastName: string;
  email: string;
  picture_id: number;
  is_analyst: boolean;
}

/**
 * Computed user properties
 */
export interface UserProfile {
  fullName: string;
  initials: string;
  displayName: string;
}

/**
 * Get user's full name
 */
export function getUserFullName(user: User | null): string {
  if (!user) return '';
  return `${user.firstName} ${user.lastName}`.trim();
}

/**
 * Get user's initials (first letter of first and last name)
 */
export function getUserInitials(user: User | null): string {
  if (!user) return '';
  const first = user.firstName?.[0] || '';
  const last = user.lastName?.[0] || '';
  return `${first}${last}`.toUpperCase();
}

/**
 * Get user's display name (prefer full name, fallback to username)
 */
export function getUserDisplayName(user: User | null): string {
  if (!user) return '';
  const fullName = getUserFullName(user);
  return fullName || user.username;
}

/**
 * Check if user is an analyst
 */
export function isAnalyst(user: User | null): boolean {
  return user?.is_analyst ?? false;
}

/**
 * Get user profile computed properties
 */
export function getUserProfile(user: User | null): UserProfile {
  return {
    fullName: getUserFullName(user),
    initials: getUserInitials(user),
    displayName: getUserDisplayName(user)
  };
}
